import type {
	WorkflowCanvasActionData,
	WorkflowCanvasActionElement,
	WorkflowCanvasControlData,
	WorkflowCanvasControlElement,
	WorkflowCanvasEdge,
	WorkflowCanvasElement,
} from "./workflow-canvas.types";

// ──────────────────────────────────────────────
// Server WorkflowGraph types (mirrors server schema)
// ──────────────────────────────────────────────

interface WorkflowGraphNext {
	id?: string;
	node: string | null;
	description?: string;
}

interface WorkflowGraphActionNode {
	id: string;
	type: "action";
	action: string;
	description: string;
	config: Record<string, unknown>;
	next: Record<string, WorkflowGraphNext>;
}

interface WorkflowGraphControlNode {
	id: string;
	type: "control";
	control: string;
	description: string;
	config: Record<string, unknown>;
	next: Record<string, WorkflowGraphNext>;
}

type WorkflowGraphNode = WorkflowGraphActionNode | WorkflowGraphControlNode;

export interface WorkflowGraph {
	head: string;
	nodes: Record<string, WorkflowGraphNode>;
}

// ──────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────

function safeStringify(value: unknown): string | undefined {
	if (value === undefined || value === null) return undefined;
	if (typeof value === "string") return value;
	return JSON.stringify(value);
}

function safeParseJson(value: string | undefined): unknown {
	if (!value) return undefined;
	return JSON.parse(value);
}

function splitCsv(value: string | undefined): string[] | undefined {
	const items = value
		?.split(",")
		.map((item) => item.trim())
		.filter((item) => item.length > 0);
	return items && items.length > 0 ? items : undefined;
}

function normalizeConditionRouteKey(route: string): "true" | "false" | null {
	const normalized = route.trim().toLowerCase();
	if (["true", "yes", "y", "has", "exist", "exists", "on"].includes(normalized)) {
		return "true";
	}
	if (["false", "no", "n", "none", "empty", "off"].includes(normalized)) {
		return "false";
	}
	return null;
}

function resolveActionType(data: WorkflowCanvasActionData): string {
	switch (data.moduleType) {
		case "db":
			return `db-${data.dbOperation ?? "read"}`;
		case "env":
			return `env-${data.envOperation ?? "get"}`;
		case "ai":
			return `llm-${data.aiResponseType ?? "natural"}`;
		case "google-form": {
			const operation = data.googleFormOperation ?? "create";
			if (operation === "responses") return "google-form-responses";
			return `google-form-${operation}`;
		}
		default:
			return MODULE_TYPE_TO_ACTION[data.moduleType] ?? "http";
	}
}

// ──────────────────────────────────────────────
// Tree layout algorithm
// ──────────────────────────────────────────────

const LAYOUT_NODE_W = 280;
const LAYOUT_NODE_H = 112;
const LAYOUT_H_GAP = 80;
const LAYOUT_V_GAP = 120;
const LAYOUT_PADDING = 48;
const LAYOUT_COLS = 3;

interface LayoutTreeNode {
	key: string;
	children: LayoutTreeNode[];
	subtreeWidth: number;
}

function computeTreeLayout(graph: WorkflowGraph): Map<string, { x: number; y: number }> {
	const positions = new Map<string, { x: number; y: number }>();
	const visited = new Set<string>();

	function buildSubtree(key: string): LayoutTreeNode | null {
		if (!key || visited.has(key) || !graph.nodes[key]) return null;
		visited.add(key);

		const node = graph.nodes[key]!;
		const children: LayoutTreeNode[] = [];

		const nextEntries = Object.entries(node.next).sort(([a], [b]) => {
			if (a === "true" || a === "next") return -1;
			if (b === "true" || b === "next") return 1;
			return a.localeCompare(b);
		});

		for (const [, nextInfo] of nextEntries) {
			if (nextInfo.node) {
				const child = buildSubtree(nextInfo.node);
				if (child) children.push(child);
			}
		}

		return { key, children, subtreeWidth: 0 };
	}

	function computeWidth(node: LayoutTreeNode): number {
		if (node.children.length === 0) {
			node.subtreeWidth = LAYOUT_NODE_W;
			return LAYOUT_NODE_W;
		}

		let total = 0;
		for (const child of node.children) {
			total += computeWidth(child);
		}
		total += (node.children.length - 1) * LAYOUT_H_GAP;
		node.subtreeWidth = Math.max(LAYOUT_NODE_W, total);
		return node.subtreeWidth;
	}

	function assignPositions(node: LayoutTreeNode, left: number, top: number): void {
		positions.set(node.key, {
			x: left + node.subtreeWidth / 2 - LAYOUT_NODE_W / 2,
			y: top,
		});

		if (node.children.length === 0) return;

		let childrenWidth = 0;
		for (const child of node.children) {
			childrenWidth += child.subtreeWidth;
		}
		childrenWidth += (node.children.length - 1) * LAYOUT_H_GAP;

		let childLeft = left + (node.subtreeWidth - childrenWidth) / 2;
		const childTop = top + LAYOUT_NODE_H + LAYOUT_V_GAP;

		for (const child of node.children) {
			assignPositions(child, childLeft, childTop);
			childLeft += child.subtreeWidth + LAYOUT_H_GAP;
		}
	}

	const root = buildSubtree(graph.head);
	if (root) {
		computeWidth(root);
		assignPositions(root, LAYOUT_PADDING, LAYOUT_PADDING);
	}

	const unvisited = Object.keys(graph.nodes).filter((k) => !visited.has(k));
	if (unvisited.length > 0) {
		let maxY = LAYOUT_PADDING;
		for (const pos of positions.values()) {
			maxY = Math.max(maxY, pos.y);
		}
		const startY = maxY + LAYOUT_NODE_H + LAYOUT_V_GAP * 2;
		for (let i = 0; i < unvisited.length; i++) {
			positions.set(unvisited[i]!, {
				x: LAYOUT_PADDING + (i % LAYOUT_COLS) * (LAYOUT_NODE_W + LAYOUT_H_GAP),
				y: startY + Math.floor(i / LAYOUT_COLS) * (LAYOUT_NODE_H + LAYOUT_V_GAP),
			});
		}
	}

	return positions;
}

// ──────────────────────────────────────────────
// Server Graph → Canvas mapping
// ──────────────────────────────────────────────

function serverActionToCanvas(
	action: string,
	config: Record<string, unknown>,
): Partial<WorkflowCanvasActionData> {
	switch (action) {
		case "http":
			return {
				moduleType: "http",
				method: (config.method as WorkflowCanvasActionData["method"]) ?? "GET",
				url: config.url as string,
				headers: safeStringify(config.headers),
				body: safeStringify(config.body),
				timeout: config.timeout as number | undefined,
			};

		case "sms":
			return {
				moduleType: "sms",
				phoneNumber: (config.receiver as string) ?? "",
				message: (config.msg as string) ?? "",
				sender: (config.sender as string) ?? "",
			};

		case "gmail":
			return {
				moduleType: "gmail",
				to: Array.isArray(config.to) ? config.to.join(", ") : (config.to as string),
				subject: config.subject as string,
				body: config.body as string,
				html: config.html !== undefined ? String(config.html) : undefined,
				attachments: safeStringify(config.attachments),
			};

		case "google-drive":
			return {
				moduleType: "google-drive",
				googleDriveOperation: config.operation as WorkflowCanvasActionData["googleDriveOperation"],
				folderId: config.folderId as string | undefined,
				fileId: config.fileId as string | undefined,
				fileName: config.fileName as string | undefined,
				content: config.content as string | undefined,
				mimeType: config.mimeType as string | undefined,
			};

		case "google-sheet":
			return {
				moduleType: "google-sheet",
				googleSheetOperation: config.operation as WorkflowCanvasActionData["googleSheetOperation"],
				spreadsheetId: config.spreadsheetId as string | undefined,
				range: config.range as string | undefined,
				values: safeStringify(config.values),
				title: config.title as string | undefined,
			};

		case "transform":
			return {
				moduleType: "transform",
				transformFrom: config.from as WorkflowCanvasActionData["transformFrom"],
				transformTo: config.to as WorkflowCanvasActionData["transformTo"],
				transformData: config.data as string | undefined,
				transformOptions: safeStringify(config.options),
			};

		case "latex":
			return {
				moduleType: "latex",
				latexExpression: config.expression as string,
				latexVariables: safeStringify(config.variables),
			};

		case "crawl":
			return {
				moduleType: "crawl",
				url: config.url as string,
				crawlSelector: config.selector as string | undefined,
				crawlExtract: config.extract as string | undefined,
				timeout: config.timeout as number | undefined,
			};

		case "llm-natural":
			return {
				moduleType: "ai",
				aiResponseType: "natural",
				aiPrompt: config.prompt as string,
				aiContext: config.context as string | undefined,
				aiMaxTokens: config.maxTokens as number | undefined,
			};

		case "llm-json":
			return {
				moduleType: "ai",
				aiResponseType: "json",
				aiPrompt: config.prompt as string,
				aiSchema: safeStringify(config.schema),
				aiContext: config.context as string | undefined,
			};

		case "llm-music":
			return {
				moduleType: "ai",
				aiResponseType: "music",
				aiPrompt: config.prompt as string,
				aiDurationSeconds: config.durationSeconds as number | undefined,
			};

		case "llm-tts":
			return {
				moduleType: "ai",
				aiResponseType: "tts",
				aiText: config.text as string,
				aiVoice: config.voice as string | undefined,
			};

		case "llm-image":
			return {
				moduleType: "ai",
				aiResponseType: "image",
				aiPrompt: config.prompt as string,
				aiSize: config.size as string | undefined,
			};

		case "db-read":
			return {
				moduleType: "db",
				dbOperation: "read",
				dbRef: config.ref as string,
				dbSelect: safeStringify(config.select),
			};

		case "db-write":
			return {
				moduleType: "db",
				dbOperation: "write",
				dbTable: config.table as string,
				dbData: safeStringify(config.data),
			};

		case "db-update":
			return {
				moduleType: "db",
				dbOperation: "update",
				dbSets: safeStringify(config.sets),
			};

		case "db-delete":
			return {
				moduleType: "db",
				dbOperation: "delete",
				dbTable: config.table as string,
				dbRef: config.ref as string,
			};

		case "env-get":
			return {
				moduleType: "env",
				envOperation: "get",
				envKey: config.key as string,
			};

		case "env-set":
			return {
				moduleType: "env",
				envOperation: "set",
				envKey: config.key as string,
				envValue: config.value as string,
			};

		case "youtube-upload":
			return {
				moduleType: "youtube",
				youtubeVideoUrl: config.videoUrl as string,
				youtubeTitle: config.title as string,
				youtubeDescription: config.description as string | undefined,
				youtubeTags: safeStringify(config.tags),
				youtubeCategoryId: config.categoryId as string | undefined,
				youtubePrivacyStatus: config.privacyStatus as WorkflowCanvasActionData["youtubePrivacyStatus"],
				youtubeMadeForKids: config.madeForKids as boolean | undefined,
			};

		default:
			return { moduleType: "generic" };
	}
}

function serverControlToCanvas(
	control: string,
	config: Record<string, unknown>,
): Partial<WorkflowCanvasControlData> {
	switch (control) {
		case "condition":
			return {
				moduleType: "condition",
				conditionExpression: config.condition as string,
				conditionTrueRoute:
					(typeof config.true === "string" && config.true.trim()) ||
					Object.keys(config).find((key) => normalizeConditionRouteKey(key) === "true") ||
					"true",
				conditionFalseRoute:
					(typeof config.false === "string" && config.false.trim()) ||
					Object.keys(config).find((key) => normalizeConditionRouteKey(key) === "false") ||
					"false",
			};
		case "loop":
			return {
				moduleType: "loop",
				loopExpression: config.over as string,
				loopAlias: (config.as as string | undefined) ?? "item",
			};
		case "delay":
			return {
				moduleType: "delay",
				delayMs: config.ms as number,
			};
		case "schedule":
			return {
				moduleType: "schedule",
				scheduleCron: config.cron as string,
			};
		default:
			return { moduleType: "generic" };
	}
}

const ACTION_NAMES: Record<string, string> = {
	http: "HTTP",
	sms: "SMS",
	gmail: "Gmail",
	"google-drive": "Google Drive",
	"google-sheet": "Google Sheet",
	transform: "Transform",
	latex: "Latex",
	crawl: "Crawling",
	"llm-natural": "AI",
	"llm-json": "AI",
	"llm-music": "AI",
	"llm-tts": "AI",
	"llm-image": "AI",
	"db-read": "DB",
	"db-write": "DB",
	"db-update": "DB",
	"db-delete": "DB",
	"env-get": "ENV",
	"env-set": "ENV",
	"youtube-upload": "YouTube",
};

const ACTION_GYUL: Record<string, number> = {
	http: 0,
	sms: 1,
	gmail: 0,
	"google-drive": 0,
	"google-sheet": 0,
	transform: 0,
	latex: 0,
	crawl: 1,
	"llm-natural": 2,
	"llm-json": 2,
	"llm-music": 2,
	"llm-tts": 2,
	"llm-image": 2,
	"db-read": 0,
	"db-write": 0,
	"db-update": 0,
	"db-delete": 0,
	"env-get": 0,
	"env-set": 0,
	"youtube-upload": 0,
};

const CONTROL_NAMES: Record<string, string> = {
	condition: "Condition",
	loop: "Loop",
	delay: "Delay",
	schedule: "Schedule",
	parallel: "Parallel",
	error: "Error",
};

function createCanvasId(): string {
	if (typeof globalThis.crypto?.randomUUID === "function") {
		return globalThis.crypto.randomUUID();
	}
	return `wf-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

// ──────────────────────────────────────────────
// Server Graph → Canvas (one-way: load only)
// ──────────────────────────────────────────────

export function definitionToCanvas(
	definition: unknown,
): { elements: WorkflowCanvasElement[]; edges: WorkflowCanvasEdge[] } | null {
	if (!definition || typeof definition !== "object") return null;

	const def = definition as Record<string, unknown>;

	if (!def.head || !def.nodes) return null;

	const graph = definition as WorkflowGraph;
	const nodeKeys = Object.keys(graph.nodes);
	const keyToCanvasId = new Map<string, string>();
	const elements: WorkflowCanvasElement[] = [];
	const treePositions = computeTreeLayout(graph);

	for (let i = 0; i < nodeKeys.length; i++) {
		const key = nodeKeys[i]!;
		const node = graph.nodes[key]!;
		const canvasId = createCanvasId();
		keyToCanvasId.set(key, canvasId);

		const pos = treePositions.get(key) ?? { x: LAYOUT_PADDING, y: LAYOUT_PADDING };

		if (node.type === "action") {
			const actionNode = node as WorkflowGraphActionNode;
			const canvasData = serverActionToCanvas(actionNode.action, actionNode.config);
			elements.push({
				id: canvasId,
				kind: "action",
				position: pos,
				data: {
					moduleType: canvasData.moduleType ?? "generic",
					name: ACTION_NAMES[actionNode.action] ?? "Action",
					description: actionNode.description,
					parameterName: key,
					gyulAmount: ACTION_GYUL[actionNode.action] ?? 1,
					...canvasData,
				},
			} as WorkflowCanvasActionElement);
		} else {
			const controlNode = node as WorkflowGraphControlNode;
			const canvasData = serverControlToCanvas(controlNode.control, controlNode.config);
			elements.push({
				id: canvasId,
				kind: "control",
				position: pos,
				data: {
					moduleType: canvasData.moduleType ?? "generic",
					name: CONTROL_NAMES[controlNode.control] ?? "Control",
					description: controlNode.description,
					parameterName: key,
					...canvasData,
				},
			} as WorkflowCanvasControlElement);
		}
	}

	const edges: WorkflowCanvasEdge[] = [];

	for (const key of nodeKeys) {
		const node = graph.nodes[key]!;
		const fromId = keyToCanvasId.get(key);
		if (!fromId) continue;

		for (const [routeKey, nextInfo] of Object.entries(node.next)) {
			if (!nextInfo.node) continue;
			const toId = keyToCanvasId.get(nextInfo.node);
			if (!toId) continue;

			let sourcePort: "bottom" | "left" | "right" = "bottom";
			if (node.type === "control" && (node as WorkflowGraphControlNode).control === "condition") {
				const conditionConfig = (node as WorkflowGraphControlNode).config as Record<string, unknown>;
				const trueRoute =
					(typeof conditionConfig.true === "string" && conditionConfig.true.trim()) ||
					"true";
				const falseRoute =
					(typeof conditionConfig.false === "string" && conditionConfig.false.trim()) ||
					"false";
				sourcePort =
					routeKey === trueRoute
						? "left"
						: routeKey === falseRoute
							? "right"
							: normalizeConditionRouteKey(routeKey) === "true"
								? "left"
								: normalizeConditionRouteKey(routeKey) === "false"
									? "right"
									: "bottom";
			}

			edges.push({
				id: createCanvasId(),
				fromElementId: fromId,
				toElementId: toId,
				label: routeKey === "next" ? undefined : routeKey,
				description: nextInfo.description,
				sourcePort,
			});
		}
	}

	return { elements, edges };
}

// ──────────────────────────────────────────────
// Canvas → Server Graph (저장 시 사용)
// ──────────────────────────────────────────────

function canvasActionToServerConfig(
	moduleType: string,
	data: WorkflowCanvasActionData,
): Record<string, unknown> {
	switch (moduleType) {
		case "sms":
			return {
				sender: (data.sender ?? "").trim() || undefined,
				receiver: (data.phoneNumber ?? "").trim(),
				msg: (data.message ?? "").trim(),
			};
		case "http":
			return {
				method: data.method ?? "GET",
				url: (data.url ?? "").trim(),
				headers: data.headers ? JSON.parse(data.headers) : undefined,
				body: data.body,
				timeout: data.timeout,
			};
		case "gmail":
			return {
				to: splitCsv(data.to),
				subject: (data.subject ?? "").trim(),
				body: data.body ?? "",
				html: data.html ?? undefined,
				attachments: safeParseJson(data.attachments),
			};
		case "google-drive":
			return {
				operation: data.googleDriveOperation ?? "list",
				folderId: (data.folderId ?? "").trim() || undefined,
				fileId: (data.fileId ?? "").trim() || undefined,
				fileName: (data.fileName ?? "").trim() || undefined,
				content: data.content ?? undefined,
				mimeType: (data.mimeType ?? "").trim() || undefined,
			};
		case "google-sheet":
			return {
				operation: data.googleSheetOperation ?? "read",
				spreadsheetId: (data.spreadsheetId ?? "").trim() || undefined,
				range: (data.range ?? "").trim() || undefined,
				values: safeParseJson(data.values),
				title: (data.title ?? "").trim() || undefined,
			};
		case "transform":
			return {
				from: data.transformFrom ?? "json",
				to: data.transformTo ?? "json",
				data: data.transformData ?? "",
				options: safeParseJson(data.transformOptions),
			};
		case "latex":
			return {
				expression: data.latexExpression ?? "",
				variables: safeParseJson(data.latexVariables),
			};
		case "crawl":
			return {
				url: (data.url ?? "").trim(),
				selector: (data.crawlSelector ?? "").trim() || undefined,
				extract: (data.crawlExtract ?? "").trim() || undefined,
				timeout: data.timeout,
			};
		case "db":
			switch (data.dbOperation) {
				case "write":
					return {
						table: (data.dbTable ?? "").trim(),
						data: safeParseJson(data.dbData),
					};
				case "update":
					return {
						sets: safeParseJson(data.dbSets),
					};
				case "delete":
					return {
						table: (data.dbTable ?? "").trim(),
						ref: (data.dbRef ?? "").trim(),
					};
				case "read":
				default:
					return {
						ref: (data.dbRef ?? "").trim(),
						select: safeParseJson(data.dbSelect),
					};
			}
		case "env":
			return {
				key: (data.envKey ?? "").trim(),
				...(data.envOperation === "set" ? { value: data.envValue ?? "" } : {}),
			};
		case "youtube":
			return {
				videoUrl: (data.youtubeVideoUrl ?? "").trim(),
				title: (data.youtubeTitle ?? "").trim(),
				description: data.youtubeDescription ?? undefined,
				tags: safeParseJson(data.youtubeTags),
				categoryId: (data.youtubeCategoryId ?? "").trim() || undefined,
				privacyStatus: data.youtubePrivacyStatus ?? "private",
				madeForKids: data.youtubeMadeForKids ?? false,
			};
		default:
			return {};
	}
}

function canvasControlToServerConfig(
	moduleType: string,
	data: WorkflowCanvasControlData,
): Record<string, unknown> {
	switch (moduleType) {
		case "condition":
			return {
				condition: data.conditionExpression ?? "",
				true: data.conditionTrueRoute?.trim() || "true",
				false: data.conditionFalseRoute?.trim() || "false",
			};
		case "loop":
			return { over: data.loopExpression ?? "", as: data.loopAlias?.trim() || "item" };
		case "delay":
			return { ms: data.delayMs ?? 0 };
		case "schedule":
			return { cron: data.scheduleCron ?? "" };
		default:
			return {};
	}
}

const MODULE_TYPE_TO_ACTION: Record<string, string> = {
	http: "http",
	sms: "sms",
	gmail: "gmail",
	"google-drive": "google-drive",
	"google-sheet": "google-sheet",
	transform: "transform",
	latex: "latex",
	crawl: "crawl",
	youtube: "youtube-upload",
};

const MODULE_TYPE_TO_CONTROL: Record<string, string> = {
	condition: "condition",
	loop: "loop",
	delay: "delay",
	schedule: "schedule",
};

/**
 * 캔버스 elements + edges → 서버 definition (graph).
 * 워크플로우 저장 시 호출하여 PATCH definition에 사용.
 */
export function canvasToDefinition(
	elements: WorkflowCanvasElement[],
	edges: WorkflowCanvasEdge[],
): WorkflowGraph | null {
	if (elements.length === 0) return null;

	const idToParam = new Map<string, string>();
	for (const el of elements) {
		const param = el.data.parameterName?.trim();
		if (param) idToParam.set(el.id, param);
	}

	const inDegree = new Map<string, number>();
	for (const el of elements) {
		inDegree.set(el.id, 0);
	}
	for (const edge of edges) {
		if (inDegree.has(edge.toElementId)) {
			inDegree.set(edge.toElementId, (inDegree.get(edge.toElementId) ?? 0) + 1);
		}
	}
	const roots = elements.filter((el) => (inDegree.get(el.id) ?? 0) === 0);
	const headId = roots[0]?.id;
	const headParam = headId ? idToParam.get(headId) : undefined;
	if (!headParam) return null;

	const elementById = new Map(elements.map((el) => [el.id, el] as const));
	const adjacency = new Map<string, Array<{ label: string; toId: string }>>();
	for (const edge of edges) {
		const fromElement = elementById.get(edge.fromElementId);
		let routeKey = edge.label ?? "next";
		if (fromElement?.kind === "control" && fromElement.data.moduleType === "condition") {
			routeKey =
				edge.sourcePort === "left"
					? fromElement.data.conditionTrueRoute?.trim() || "true"
					: edge.sourcePort === "right"
						? fromElement.data.conditionFalseRoute?.trim() || "false"
						: routeKey;
		}
		if (fromElement?.kind === "control" && fromElement.data.moduleType === "loop") {
			routeKey = "next";
		}
		const list = adjacency.get(edge.fromElementId) ?? [];
		list.push({ label: routeKey, toId: edge.toElementId });
		adjacency.set(edge.fromElementId, list);
	}

	const nodes: Record<string, WorkflowGraphNode> = {};

	for (const el of elements) {
		const param = idToParam.get(el.id);
		if (!param) continue;

		const nextList = adjacency.get(el.id) ?? [];
		const next: Record<string, WorkflowGraphNext> = {};
		for (const { label, toId } of nextList) {
			const toParam = idToParam.get(toId);
			next[label || "next"] = { node: toParam ?? null };
		}
		if (Object.keys(next).length === 0) {
			next.next = { node: null };
		}

		if (el.kind === "action") {
			const actionData = el.data as WorkflowCanvasActionData;
			const action = resolveActionType(actionData);
			nodes[param] = {
				id: param,
				type: "action",
				action,
				description: actionData.description ?? "",
				config: canvasActionToServerConfig(actionData.moduleType, actionData),
				next,
			};
		} else {
			const controlData = el.data as WorkflowCanvasControlData;
			const control = MODULE_TYPE_TO_CONTROL[controlData.moduleType] ?? "condition";
			nodes[param] = {
				id: param,
				type: "control",
				control,
				description: controlData.description ?? "",
				config: canvasControlToServerConfig(controlData.moduleType, controlData),
				next,
			};
		}
	}

	return { head: headParam, nodes };
}
